import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LumisialProvider } from './context/LumisialContext';
import { RegularizacionProvider } from './context/RegularizacionContext';
import { VentanaRegularizacion } from './components/comprobantes/VentanaRegularizacion';
import { ProfileModalProvider } from './context/ProfileModalContext';
import { ToastProvider } from './components/ui/Toast';
import { ProfileModal } from './components/ProfileModal';
import { ReportarProblemaModal, ReportarProblemaProvider } from './components/ReportarProblemaModal';
import ErrorBoundary from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import ContextSelectorPage from './pages/ContextSelectorPage';
import AdminLayout from './components/AdminLayout';
import { GestionUsuariosPage } from './pages/GestionUsuariosPage';
import { AdminGeografiaPage } from './pages/AdminGeografiaPage';
import IndicadoresPage from './pages/IndicadoresPage';
import AdminActividadesPage from './pages/AdminActividadesPage';
import { DetalleActividadPage } from './pages/DetalleActividadPage';
import AdminCampanasPage from './pages/AdminCampanasPage';
import AdminCuotaIglesiaPage from './pages/AdminCuotaIglesiaPage';
import { AdminBalancePage } from './pages/AdminBalancePage';
import { AdminMovimientosPage } from './pages/AdminMovimientosPage';
import DemosPage from './pages/DemosPage';
import { GestionVersionesPage } from './pages/GestionVersionesPage';
import { LumisialRoutes } from './routes/LumisialRoutes';
import { canAccessContext } from './utils/permissions';
import { isAdminGlobal } from './utils/roleHierarchy';
import { RegistroMiembroPage } from './pages/RegistroMiembroPage';
import { ScrollAlTope } from './components/ScrollAlTope';
import { OperacionActividadPendiente } from './components/OperacionActividadPendiente';

function ProtectedRoute({ children, requiredContext }: Readonly<{ children: React.ReactNode; requiredContext?: 'admin' | 'lumisial' }>) {
  const { isAuthenticated, loading, usuario } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredContext && !canAccessContext(usuario, requiredContext)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function DetalleActividadAdminWrapper() {
  const { actividadId } = useParams<{ actividadId: string }>();
  const navigate = useNavigate();
  return (
    <DetalleActividadPage
      actividadId={actividadId!}
      onBack={() => navigate('/admin/actividades')}
    />
  );
}

function AppRoutes() {
  const { usuario } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ContextSelectorPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <IndicadoresPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <GestionUsuariosPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/actividades"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminActividadesPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/actividades/:actividadId"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <DetalleActividadAdminWrapper />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/campanas"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminCampanasPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/cuota-iglesia"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminCuotaIglesiaPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/balance/movimientos"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminMovimientosPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route path="/admin/movimientos" element={<Navigate to="/admin/balance/movimientos" replace />} />

      <Route
        path="/admin/balance"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminBalancePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/geografia"
        element={
          <ProtectedRoute requiredContext="admin">
            <AdminLayout>
              <AdminGeografiaPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/versiones"
        element={
          <ProtectedRoute requiredContext="admin">
            {usuario && isAdminGlobal(usuario.rol?.nombre || '') ? (
              <AdminLayout>
                <GestionVersionesPage />
              </AdminLayout>
            ) : (
              <Navigate to="/admin" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/demos"
        element={
          <ProtectedRoute requiredContext="admin">
            {usuario && isAdminGlobal(usuario.rol?.nombre || '') ? (
              <AdminLayout>
                <DemosPage />
              </AdminLayout>
            ) : (
              <Navigate to="/admin" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/lumisial/*"
        element={
          <ProtectedRoute requiredContext="lumisial">
            <LumisialRoutes />
          </ProtectedRoute>
        }
      />

      <Route path="/registro/:token" element={<RegistroMiembroPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollAlTope />
        <AuthProvider>
          <LumisialProvider>
            <ProfileModalProvider>
              <ReportarProblemaProvider>
                <ToastProvider>
                  <RegularizacionProvider>
                    <AppRoutes />
                    <VentanaRegularizacion />
                    <OperacionActividadPendiente />
                    <ProfileModal />
                    <ReportarProblemaModal />
                    <SpeedInsights />
                    <Analytics />
                  </RegularizacionProvider>
                </ToastProvider>
              </ReportarProblemaProvider>
            </ProfileModalProvider>
          </LumisialProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
